import { CAMERA_ICON_PATH_DATA } from '../constants';
import { resolveCameraTransform } from '../geometry/resolve-camera-transform';
import { createSvgElement } from '../svg';
import type { MapRenderCameraModel } from '../types';

/**
 * Builds the SVG group for all camera icons in a map scene.
 *
 * @param cameras Immutable camera placements to convert into icon elements.
 * @returns Detached group carrying the stable `data-map-layer="cameras"` hook.
 */
export function renderCamerasLayer(cameras: readonly MapRenderCameraModel[]): SVGGElement {
  const layerElement = createSvgElement('g');
  layerElement.dataset.mapLayer = 'cameras';

  for (const camera of cameras) {
    layerElement.append(createCameraElement(camera));
  }

  return layerElement;
}

/**
 * Builds one semantic camera icon for full rendering or a partial camera update.
 *
 * @param camera Immutable camera placement providing icon style, transform and semantic identifiers.
 * @returns Detached group with `data-map-entity-kind="camera"` and canonical placement/device IDs.
 */
export function createCameraElement(camera: MapRenderCameraModel): SVGGElement {
  const transform = resolveCameraTransform(camera);
  const cameraElement = createSvgElement('g');
  cameraElement.setAttribute('aria-hidden', 'true');
  cameraElement.dataset.mapEntityKind = 'camera';
  cameraElement.dataset.mapEntityId = String(camera.mapPositionId);
  cameraElement.dataset.mapCameraId = String(camera.cameraId);

  if (transform.rotation !== null) {
    cameraElement.setAttribute('transform', transform.rotation);
  }

  const iconTransformElement = createSvgElement('g');
  iconTransformElement.setAttribute('transform', transform.icon);

  const iconPathElement = createSvgElement('path');
  iconPathElement.setAttribute('d', CAMERA_ICON_PATH_DATA);
  iconPathElement.setAttribute('fill', camera.icon.color);
  iconTransformElement.append(iconPathElement);
  cameraElement.append(iconTransformElement);

  return cameraElement;
}
